import { create } from 'zustand';
import { timeEntriesAPI, expensesAPI, searchAPI, TimeEntry, Expense, NoteSearchMatch } from '../services/api';

interface SearchState {
  timeEntries: TimeEntry[];
  expenses: Expense[];
  noteMatches: NoteSearchMatch[];
  isLoadingTimeEntries: boolean;
  isLoadingExpenses: boolean;
  isLoadingNotes: boolean;
  hasSearched: boolean;
  error: string | null;

  searchTimeEntries: (params: {
    project_id: string;
    search: string;
    start_date?: string;
    end_date?: string;
    category?: string;
    subproject_id?: string;
  }) => Promise<void>;

  searchExpenses: (params: {
    project_id: string;
    search: string;
    start_date?: string;
    end_date?: string;
  }) => Promise<void>;

  searchNotes: (params: {
    q: string;
    project_id?: string;
    start_date?: string;
    end_date?: string;
    category?: string;
    subproject_id?: string;
  }) => Promise<void>;

  clearResults: () => void;
  clearError: () => void;
}

const getErrorMessage = (error: any, defaultMsg: string): string => {
  const detail = error.response?.data?.detail;
  if (Array.isArray(detail)) {
    return detail.map((e: any) => `${e.loc?.slice(1).join('.')}: ${e.msg}`).join(', ') || defaultMsg;
  }
  if (typeof detail === 'string') {
    return detail;
  }
  return defaultMsg;
};

export const useSearchStore = create<SearchState>((set) => ({
  timeEntries: [],
  expenses: [],
  noteMatches: [],
  isLoadingTimeEntries: false,
  isLoadingExpenses: false,
  isLoadingNotes: false,
  hasSearched: false,
  error: null,

  searchTimeEntries: async (params) => {
    set({ isLoadingTimeEntries: true, error: null });
    try {
      const timeEntries = await timeEntriesAPI.list(params);
      set({ timeEntries, isLoadingTimeEntries: false, hasSearched: true });
    } catch (error: any) {
      set({
        error: getErrorMessage(error, 'Failed to search time entries'),
        isLoadingTimeEntries: false,
        hasSearched: true,
      });
    }
  },

  searchExpenses: async (params) => {
    set({ isLoadingExpenses: true, error: null });
    try {
      const expenses = await expensesAPI.list(params);
      set({ expenses, isLoadingExpenses: false, hasSearched: true });
    } catch (error: any) {
      set({
        error: getErrorMessage(error, 'Failed to search expenses'),
        isLoadingExpenses: false,
        hasSearched: true,
      });
    }
  },

  searchNotes: async (params) => {
    set({ isLoadingNotes: true, error: null });
    try {
      const noteMatches = await searchAPI.notes(params);
      set({ noteMatches, isLoadingNotes: false, hasSearched: true });
    } catch (error: any) {
      set({
        error: getErrorMessage(error, 'Failed to search notes'),
        isLoadingNotes: false,
        hasSearched: true,
      });
    }
  },

  clearResults: () => set({ timeEntries: [], expenses: [], noteMatches: [], hasSearched: false, error: null }),
  clearError: () => set({ error: null }),
}));
